// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { addToChainAfter, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import {
    chain,
    PopulateApiParameter,
    SendTracking,
} from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/PlaceOrder";
import { CustomAppState, xGoogleAnalyticsState } from "@west/App/Store/Reducers";
import { trimStringValuesInObject } from "@west/XDL/Utilities";
import { customTrackCompletedOrder, getDataLayer } from "@west/XDL/Utilities/tracking";

interface CustomBillingAddressModel {
    firstName?: string;
    lastName?: string;
    companyName?: string;
    phone?: string;
    email?: string;
}

/**
 * XNG-WEST-968
 * @param props
 * @constructor
 */
export const XngPopulateApiParameter: typeof PopulateApiParameter = props => {
    // Only add billing address if not the same as shipping
    if (props.getState().pages.onePageCheckout.isBillingAddressTheSameAsShipping) {
        return;
    }

    // Get the values from the form + the fields that are configured to display
    // trim field values with extra side spaces to avoid issues
    const billingAddressFormState = trimStringValuesInObject({
        ...(props.getState().pages.onePageCheckout.billingAddressFormState?.address as any),
    });

    // If no value, skip
    if (!billingAddressFormState) {
        return;
    }

    // Get the address fields that are enabled
    const billToAddressFields = getAddressFieldsDataView(props.getState()).value?.billToAddressFields as any;
    const billingAddressKeys = Object.keys(billToAddressFields || {}).filter(key => {
        return !!(billToAddressFields && billToAddressFields[key] && billToAddressFields[key].isVisible);
    });

    // Helper method to extract from form
    const getFormValue = (key: string): string | undefined => {
        return billingAddressKeys.indexOf(key) >= 0 &&
            billingAddressFormState[key] &&
            billingAddressFormState[key].length > 0
            ? billingAddressFormState[key]
            : undefined;
    };

    // Set up the base model
    const billingAddress: CustomBillingAddressModel = {
        firstName: getFormValue("firstName"),
        lastName: getFormValue("lastName"),
        companyName: getFormValue("companyName"),
        phone: getFormValue("phone"),
        email: getFormValue("email"),
    };

    props.apiParameter.cart.properties = {
        billingAddress: JSON.stringify(billingAddress),
    };

    // Also set the OOTB CC billing fields
    const creditCard = props.apiParameter.cart.paymentOptions?.creditCard;

    const addresses: string[] = [
        billingAddressFormState.address1,
        billingAddressFormState.address2,
        billingAddressFormState.address3,
        billingAddressFormState.address4,
    ].filter(address => !!address);

    if (creditCard) {
        creditCard.useBillingAddress = false;
        creditCard.address1 = addresses.join("|");
        creditCard.city = billingAddressFormState.city;
        creditCard.state = billingAddressFormState.state?.name || "";
        creditCard.stateAbbreviation = billingAddressFormState.state?.abbreviation || "";
        creditCard.country = billingAddressFormState.country?.name || "";
        creditCard.countryAbbreviation = billingAddressFormState.country?.abbreviation || "";
        creditCard.postalCode = billingAddressFormState.postalCode;
    }
};

// XNG-Changes: send billTo data in the transaction complete dataLayer
export const XngSendTracking: typeof SendTracking = props => {
    const updatedCart = { ...props.cartToUpdate, orderNumber: props.apiResult.cart.orderNumber };
    const session = getSession(props.getState());
    customTrackCompletedOrder(updatedCart, props.apiResult.billTo, session);
};

export const _GaTrackPurchase: typeof SendTracking = props => {
    const state = props.getState() as xGoogleAnalyticsState;
    const session = getSession(state);

    if (state.xGoogleAnalytics.purchaseEvent) {
        const dataLayer = getDataLayer();
        const updatedCart = { ...props.cartToUpdate, orderNumber: props.apiResult.cart.orderNumber };

        if (!dataLayer) {
            return;
        }

        const { apiResult } = props;

        const googleAnalyticsData = {
            event: state.xGoogleAnalytics.purchaseEventName,
            ecommerce: {
                transaction_id: apiResult.cart.orderNumber,
                value: apiResult.cart.orderSubTotal,
                tax: apiResult.cart.totalTax,
                shipping: apiResult.cart.shippingAndHandling,
                currency: session.currency?.currencyCode,
                coupon: apiResult.cart.promotionCode,
                affiliation: apiResult.billTo?.companyName,
                items: updatedCart.cartLines?.map((cartLine, idx) => ({
                    item_id: cartLine.erpNumber,
                    item_name: cartLine.shortDescription,
                    affiliation: apiResult.billTo?.companyName,
                    coupon: apiResult.cart.promotionCode,
                    discount: cartLine.pricing
                        ? cartLine.pricing.unitNetPrice < cartLine.pricing.unitListPrice
                            ? cartLine.pricing.unitListPrice - cartLine.pricing.unitNetPrice
                            : 0
                        : 0,
                    index: idx,
                    item_brand: state.xGoogleAnalytics.includeProductBrand ? cartLine.brand?.name || "" : undefined,
                    item_category: state.xGoogleAnalytics.includeProductCategory
                        ? cartLine.properties?.["category"] || ""
                        : undefined,
                    item_list_id: state.xGoogleAnalytics.includeProductCategory
                        ? cartLine.properties?.["categoryId"] || ""
                        : undefined,
                    locationId: session.pickUpWarehouse?.id,
                    price: cartLine.pricing?.unitNetPrice,
                    quantity: cartLine.qtyOrdered,
                })),
            },
        };

        dataLayer.push(googleAnalyticsData);
        dataLayer.push({ ecommerce: null });
    }
};

addToChainAfter(chain, PopulateApiParameter, XngPopulateApiParameter);
replaceInChain(chain, SendTracking, XngSendTracking);
addToChainAfter(chain, XngSendTracking, _GaTrackPurchase);
